import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Save,
  Building2,
  Pencil,
  Plus,
  Upload,
  X,
} from 'lucide-react';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useOrganizationMutations } from '../hooks/useOrganizationMutations';
import type { UpdateOrganizationInput } from '@threadheads/shared';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { BrandingSettingsCard } from './BrandingSettingsCard';
import { NavigationSettingsCard } from './NavigationSettingsCard';
import { useOrgRoleSurfaces } from '@/hooks/useOrgRoleSurfaces';

// Type-safe badge variant mappings
type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline' | 'success' | 'info' | 'warning' | 'processing' | 'deleted';

/**
 * Label + badge styling per plan, in ONE map so adding a plan is one entry that
 * cannot half-drift. Without an explicit label `plan_type` renders as a
 * title-cased slug ("Production_king"); an unlisted plan falls back to
 * title-casing the raw value, so a new DB plan degrades readably.
 */
const PLANS: Record<string, { label: string; variant: BadgeVariant }> = {
  trial: { label: 'Trial', variant: 'secondary' },
  starter: { label: 'Starter', variant: 'default' },
  professional: { label: 'Professional', variant: 'default' },
  enterprise: { label: 'Enterprise', variant: 'destructive' },
  production_king: { label: 'Production King', variant: 'default' },
  client: { label: 'Client', variant: 'secondary' },
};

const SUBSCRIPTION_BADGE_VARIANTS: Record<string, BadgeVariant> = {
  active: 'success',
  trialing: 'default',
  // The DB CHECK allows suspended/cancelled (British spelling); the Stripe
  // vocabulary below is kept for when subscription state is synced from Stripe.
  suspended: 'destructive',
  cancelled: 'secondary',
  past_due: 'destructive',
  canceled: 'secondary',
  paused: 'secondary',
};

export default function OrganizationSettings() {
  const { organization } = useOrganization();
  // Wait for the org-shape verdict before hiding, so a client doesn't watch the
  // Brands card render and then vanish.
  const { isPureClient, isLoading: surfacesLoading } = useOrgRoleSurfaces();
  const hideBrandsForClient = !surfacesLoading && isPureClient;
  const {
    updateOrganization,
  } = useOrganizationMutations();

  const [formData, setFormData] = useState({
    name: '',
  });

  const [hasChanges, setHasChanges] = useState(false);
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);

  // Initialize form data when organization loads
  useEffect(() => {
    if (!organization) return;
    setFormData({
      name: organization.name || '',
    });
  }, [organization]);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleThumbnailUpload = async (file: File) => {
    if (!organization?.id) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or SVG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2 MB');
      return;
    }
    setThumbnailUploading(true);
    try {
      const ext = file.name.split('.').pop();
      const safeName = `thumbnails/${organization.id}_${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('product_images')
        .upload(safeName, file, { cacheControl: '3600', upsert: true });
      if (uploadError) throw uploadError;
      const { data: { publicUrl } } = supabase.storage
        .from('product_images')
        .getPublicUrl(safeName);
      updateOrganization.mutate({ thumbnailUrl: publicUrl });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload thumbnail');
    } finally {
      setThumbnailUploading(false);
    }
  };

  const handleRemoveThumbnail = () => {
    updateOrganization.mutate({ thumbnailUrl: null });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const updates: UpdateOrganizationInput = {};

    // Regular fields - check for changes
    if (formData.name !== organization?.name) {
      updates.name = formData.name;
    }

    if (Object.keys(updates as Record<string, unknown>).length === 0) {
      return;
    }

    updateOrganization.mutate(updates, {
      onSuccess: () => {
        setHasChanges(false);
      },
    });
  };

  const handleReset = () => {
    if (organization) {
      setFormData({
        name: organization.name || '',
      });
      setHasChanges(false);
    }
  };

  const getPlanBadge = () => {
    const plan = organization?.plan_type;
    if (!plan) {
      return <Badge variant="secondary">Unknown</Badge>;
    }
    const entry = PLANS[plan];
    return (
      <Badge variant={entry?.variant ?? 'default'}>
        {entry?.label ?? plan.charAt(0).toUpperCase() + plan.slice(1)}
      </Badge>
    );
  };

  const getSubscriptionBadge = () => {
    const status = organization?.subscription_status ?? 'unknown';
    const variant = SUBSCRIPTION_BADGE_VARIANTS[status] || 'default';

    if (!organization?.subscription_status) {
      return <Badge variant="secondary">Unknown</Badge>;
    }

    return (
      <Badge variant={variant}>
        {organization.subscription_status.charAt(0).toUpperCase() + organization.subscription_status.slice(1)}
      </Badge>
    );
  };

  // -- Company Information card state --
  const companyInfo = organization?.settings?.company;

  const [companyEditing, setCompanyEditing] = useState(false);
  const [companyName, setCompanyName] = useState(companyInfo?.name ?? '');
  const [companyAddress, setCompanyAddress] = useState(companyInfo?.address ?? '');
  const [companyLogoUrl, setCompanyLogoUrl] = useState(companyInfo?.logoUrl ?? '');
  const [logoUploading, setLogoUploading] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  // Sync company fields when organization data changes
  useEffect(() => {
    const info = organization?.settings?.company;
    setCompanyName(info?.name ?? '');
    setCompanyAddress(info?.address ?? '');
    setCompanyLogoUrl(info?.logoUrl ?? '');
    setCompanyEditing(false);
  }, [organization]);

  const handleLogoUpload = async (file: File) => {
    if (!organization?.id) return;
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPG, PNG, or SVG image');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('Image must be under 2 MB');
      return;
    }
    setLogoUploading(true);
    try {
      const timestamp = Date.now();
      const ext = file.name.split('.').pop();
      const safeName = `logos/${organization.id}_${timestamp}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('product_images')
        .upload(safeName, file, { cacheControl: '3600', upsert: true });

      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('product_images')
        .getPublicUrl(safeName);

      setCompanyLogoUrl(publicUrl);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setLogoUploading(false);
    }
  };

  const handleCompanySave = () => {
    const currentSettings = organization?.settings ?? {};
    // `brands` is `.optional()` on the schema (matches every sibling settings
    // block), so the plain spread typechecks — no cast needed.
    const updates: UpdateOrganizationInput = {
      settings: {
        ...currentSettings,
        company: {
          name: companyName.trim() || undefined,
          address: companyAddress.trim() || undefined,
          logoUrl: companyLogoUrl.trim() || undefined,
        },
      },
    };

    updateOrganization.mutate(updates, {
      onSuccess: () => {
        setCompanyEditing(false);
      },
    });
  };

  const handleCompanyCancel = () => {
    const info = organization?.settings?.company;
    setCompanyName(info?.name ?? '');
    setCompanyAddress(info?.address ?? '');
    setCompanyLogoUrl(info?.logoUrl ?? '');
    setCompanyEditing(false);
  };

  // -- Brands manager state --
  // Cast-gated read: works whether or not the built @threadheads/shared type
  // carries `brands` yet (the schema change may land in a parallel PR / a stale
  // dist). Filters to trimmed non-empty strings defensively.
  const MAX_BRANDS = 50;
  const readSavedBrands = (): string[] => {
    const saved = (organization?.settings as { brands?: unknown } | null | undefined)?.brands;
    return Array.isArray(saved)
      ? saved.filter((b): b is string => typeof b === 'string' && b.trim() !== '')
      : [];
  };

  const [brands, setBrands] = useState<string[]>([]);
  const [brandDraft, setBrandDraft] = useState('');
  const [brandsDirty, setBrandsDirty] = useState(false);

  // Sync brands when organization data changes (mirrors the company card sync).
  useEffect(() => {
    setBrands(readSavedBrands());
    setBrandDraft('');
    setBrandsDirty(false);
    // readSavedBrands closes over `organization`; re-run on org change only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const addBrand = () => {
    const v = brandDraft.trim();
    if (!v) return;
    if (brands.length >= MAX_BRANDS) {
      toast.error(`You can add up to ${MAX_BRANDS} brands.`);
      return;
    }
    // Dedupe case-insensitive — silently no-op on a repeat, just clear the input.
    if (brands.some((b) => b.toLowerCase() === v.toLowerCase())) {
      setBrandDraft('');
      return;
    }
    setBrands((prev) => [...prev, v]);
    setBrandDraft('');
    setBrandsDirty(true);
  };

  const removeBrand = (brand: string) => {
    setBrands((prev) => prev.filter((b) => b !== brand));
    setBrandsDirty(true);
  };

  const handleBrandsSave = () => {
    const currentSettings = organization?.settings ?? {};
    const updates = {
      settings: { ...currentSettings, brands },
    } as unknown as UpdateOrganizationInput;
    updateOrganization.mutate(updates, {
      onSuccess: () => setBrandsDirty(false),
    });
  };

  const handleBrandsCancel = () => {
    setBrands(readSavedBrands());
    setBrandDraft('');
    setBrandsDirty(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        {/* Company Information Card */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle>Company Information</CardTitle>
              <CardDescription>
                Details shown on purchase orders and other documents
              </CardDescription>
            </div>
            {!companyEditing && (
              <Button type="button" variant="outline" size="sm" onClick={() => setCompanyEditing(true)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {companyEditing ? (
              <div className="space-y-4">
                {/* Logo upload */}
                <div className="space-y-2">
                  <Label>Logo</Label>
                  <div className="flex items-center gap-4">
                    {companyLogoUrl ? (
                      <div className="relative h-16 w-16 rounded border bg-muted flex items-center justify-center overflow-hidden">
                        <img src={companyLogoUrl} alt="Company logo" className="h-full w-full object-contain" />
                        <button
                          type="button"
                          className="absolute -top-1 -right-1 rounded-full bg-destructive text-destructive-foreground h-5 w-5 flex items-center justify-center"
                          onClick={() => setCompanyLogoUrl('')}
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ) : (
                      <div className="h-16 w-16 rounded border border-dashed bg-muted flex items-center justify-center">
                        <Building2 className="h-6 w-6 text-muted-foreground" />
                      </div>
                    )}
                    <div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={logoUploading}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {logoUploading ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload className="mr-2 h-4 w-4" />
                            Upload Logo
                          </>
                        )}
                      </Button>
                      <p className="text-xs text-muted-foreground mt-1">PNG, JPG, or SVG. Recommended 200x200px.</p>
                      <input
                        ref={logoInputRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) void handleLogoUpload(file);
                          e.target.value = '';
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Company Name */}
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    type="text"
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    placeholder="e.g. ThreadHeads Pty Ltd"
                  />
                  <p className="text-xs text-muted-foreground">
                    The name printed on documents. Falls back to your organization name if empty.
                  </p>
                </div>

                {/* Company Address */}
                <div className="space-y-2">
                  <Label htmlFor="company_address">Company Address</Label>
                  <Textarea
                    id="company_address"
                    value={companyAddress}
                    onChange={(e) => setCompanyAddress(e.target.value)}
                    placeholder={"123 Main Street\nSydney NSW 2000\nAustralia"}
                    rows={3}
                  />
                </div>

                {/* Save / Cancel */}
                <div className="flex gap-2 pt-2">
                  <Button
                    type="button"
                    onClick={handleCompanySave}
                    disabled={updateOrganization.isPending}
                  >
                    {updateOrganization.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="mr-2 h-4 w-4" />
                        Save
                      </>
                    )}
                  </Button>
                  <Button type="button" variant="outline" onClick={handleCompanyCancel}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-4">
                {/* Logo preview */}
                {companyLogoUrl ? (
                  <div className="h-16 w-16 rounded border bg-muted flex items-center justify-center overflow-hidden shrink-0">
                    <img src={companyLogoUrl} alt="Company logo" className="h-full w-full object-contain" />
                  </div>
                ) : (
                  <div className="h-16 w-16 rounded border border-dashed bg-muted flex items-center justify-center shrink-0">
                    <Building2 className="h-6 w-6 text-muted-foreground" />
                  </div>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {companyName || <span className="text-muted-foreground italic">No company name set</span>}
                  </p>
                  {companyAddress ? (
                    <p className="text-sm text-muted-foreground whitespace-pre-line mt-1">{companyAddress}</p>
                  ) : (
                    <p className="text-sm text-muted-foreground italic mt-1">No address set</p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Branding Card */}
        <BrandingSettingsCard />

        <NavigationSettingsCard />

        {/*
          Brands Card — hidden for pure-client 3PL orgs (brands drive Product Dev,
          which clients don't run). Render-level only: this card owns its own
          writer (handleBrandsSave) and the outer form submit sends nothing but
          `name`, so hiding it cannot touch the saved value. Do NOT "preserve"
          brands from any other save path — updateOrganization REPLACES the whole
          settings JSONB and only re-attaches inventoryPush/papSync/billing, so
          adding a second writer is how brands would get destroyed.
        */}
        {!hideBrandsForClient && (
        <Card>
          <CardHeader>
            <CardTitle>Brands</CardTitle>
            <CardDescription>
              Brands power the Product Dev brand dropdown and grouping.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {brands.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {brands.map((brand) => (
                  <span
                    key={brand}
                    className="inline-flex items-center gap-1 rounded-full border bg-muted/50 py-1 pl-3 pr-1.5 text-sm"
                  >
                    {brand}
                    <button
                      type="button"
                      onClick={() => removeBrand(brand)}
                      aria-label={`Remove ${brand}`}
                      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-sm italic text-muted-foreground">
                No brands yet. Add your first below.
              </p>
            )}

            <div className="flex items-center gap-2">
              <Input
                value={brandDraft}
                onChange={(e) => setBrandDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    addBrand();
                  }
                }}
                placeholder="e.g. Please Hold"
                maxLength={120}
                aria-label="Add a brand"
                className="max-w-xs"
                disabled={brands.length >= MAX_BRANDS}
              />
              <Button
                type="button"
                variant="outline"
                onClick={addBrand}
                disabled={!brandDraft.trim() || brands.length >= MAX_BRANDS}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add
              </Button>
            </div>

            {brands.length >= MAX_BRANDS && (
              <p className="text-xs text-muted-foreground">
                You've reached the maximum of {MAX_BRANDS} brands.
              </p>
            )}

            {brandsDirty && (
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  onClick={handleBrandsSave}
                  disabled={updateOrganization.isPending}
                >
                  {updateOrganization.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save
                    </>
                  )}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleBrandsCancel}
                  disabled={updateOrganization.isPending}
                >
                  Cancel
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Organization Info Card */}
        <Card>
          <CardHeader>
            <CardTitle>Organization Details</CardTitle>
            <CardDescription>
              View and manage your organization's basic information
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Read-only info */}
            <div className="grid grid-cols-2 gap-4 p-4 bg-muted rounded-lg">
              <div>
                <Label className="text-sm text-muted-foreground">Organization ID</Label>
                <p className="text-sm font-mono mt-1">{organization?.id ?? '-'}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Slug</Label>
                <p className="text-sm font-mono mt-1">{organization?.slug ?? '-'}</p>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Plan</Label>
                <div className="mt-1">{getPlanBadge()}</div>
              </div>
              <div>
                <Label className="text-sm text-muted-foreground">Subscription Status</Label>
                <div className="mt-1">{getSubscriptionBadge()}</div>
              </div>
            </div>

            {/* Editable fields */}
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="org_name">Organization Name</Label>
                <Input
                  id="org_name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="Acme Corporation"
                  required
                />
              </div>

              <div className="space-y-2">
                <Label>Store Thumbnail</Label>
                <p className="text-xs text-muted-foreground">Square image shown in the store switcher. JPG, PNG, or SVG, max 2 MB.</p>
                <div className="flex items-center gap-3">
                  {organization?.thumbnail_url ? (
                    <img
                      src={organization.thumbnail_url}
                      alt="Store thumbnail"
                      className="h-10 w-10 rounded-md object-cover border"
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-md border border-dashed flex items-center justify-center text-muted-foreground">
                      <Upload className="h-4 w-4" />
                    </div>
                  )}
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={thumbnailUploading}
                      onClick={() => thumbnailInputRef.current?.click()}
                    >
                      {thumbnailUploading ? <Loader2 className="mr-2 h-3 w-3 animate-spin" /> : <Upload className="mr-2 h-3 w-3" />}
                      {organization?.thumbnail_url ? 'Replace' : 'Upload'}
                    </Button>
                    {organization?.thumbnail_url && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={handleRemoveThumbnail}
                      >
                        <X className="mr-1 h-3 w-3" /> Remove
                      </Button>
                    )}
                  </div>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/svg+xml"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleThumbnailUpload(file);
                      e.target.value = '';
                    }}
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        {hasChanges && (
          <Card>
            <CardContent className="flex items-center justify-between py-4">
              <p className="text-sm text-muted-foreground">
                You have unsaved changes
              </p>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleReset}
                  disabled={updateOrganization.isPending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={updateOrganization.isPending}
                >
                  {updateOrganization.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </form>
  );
}
